import React from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { AppShell } from 'shared/components/AppShell';
import { getManagerNav } from '../../navigation';
import { useAuth } from 'shared/contexts/AuthContext';
import { useManager } from '../../contexts/ManagerContext';
import { CustomerPicker } from '../CustomerPicker';
import { CreateMenu } from '../CreateMenu';

/**
 * Manager area shell: navy header + manager sidebar, wrapping every manager
 * route. Self-serve manager signup (aligned's 3-step onboarding wizard,
 * gated on `Manager.isFirstTime`) is not ported — see the 2026-07-06 decision
 * log entry — so this never redirects on `isFirstTime`; a resolved manager
 * account goes straight to the app.
 */
export function ManagerLayout() {
  const { pathname } = useLocation();
  const { isAdmin } = useAuth();
  const { manager, loading, error } = useManager();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center px-6">
        <p className="max-w-sm text-center text-sm text-amber-800">{error}</p>
      </div>
    );
  }

  const nav = getManagerNav({ isAdmin, manager });
  const managerPathSegments = pathname.split('/').filter(Boolean);
  const isTemplateBuilder = managerPathSegments[0] === 'manager'
    && managerPathSegments[1] === 'template-builder'
    && managerPathSegments.length > 2;
  const isSyntheticData = managerPathSegments[0] === 'manager'
    && managerPathSegments[1] === 'tools'
    && managerPathSegments[2] === 'auto-tune';

  return (
    <AppShell
      nav={nav}
      fullWidth={isTemplateBuilder || isSyntheticData}
      fullHeight={isSyntheticData}
      sidebarHeader={manager && !manager.isSelfServe ? <CreateMenu /> : null}
      headerActions={<CustomerPicker />}
    >
      <Outlet />
    </AppShell>
  );
}
